import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http, API_BASE } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";
import ScienceNav from "./ScienceNav";
import PageGuide from "@/components/PageGuide";
import { stampGuide } from "@/pages/NaturePassport";
import GuidePdfDownload from "@/components/GuidePdfDownload";
import { ArrowRight } from "@phosphor-icons/react";

/** Pillar page — subjects → topics, plus the published concepts in this pillar. */
export default function SciencePillar() {
  const { pillar } = useParams();
  const [meta, setMeta] = useState(null);
  const [concepts, setConcepts] = useState(null);

  useEffect(() => {
    setMeta(null); setConcepts(null);
    stampGuide(`science:pillar:${pillar}`);
    http.get("/science/taxonomy").then(({ data }) => {
      setMeta((data.pillars || []).find((p) => p.slug === pillar) || null);
    }).catch(() => {});
    http.get("/science/concepts", { params: { pillar, limit: 100 } })
      .then(({ data }) => setConcepts(data.concepts || []))
      .catch(() => setConcepts([]));
  }, [pillar]);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid={`science-pillar-page-${pillar}`}>
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <ScienceNav />
        <div className="mb-6"><PageGuide pageKey={`science:pillar:${pillar}`} testidPrefix="science-pillar" /></div>
        <SeoMeta
          title={meta ? `${meta.label} — Science Pulse` : "Science Pulse"}
          description={meta ? `${meta.label}: ${meta.subjects.length} subjects — every published concept explained at four levels, from curious to expert, with evidence labels.` : "One interconnected map of science, explained at four levels."}
          canonical={`${window.location.origin}/science/${pillar}`}
        />
        {!meta ? (
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase">Loading…</div>
        ) : (
          <>
            <div className="text-3xl">{meta.emoji}</div>
            <h1 className="mt-1 font-serif-display text-4xl md:text-5xl tracking-tight">{meta.label}.</h1>
            <p className="mt-2 text-sm text-[#3A3830] max-w-2xl">
              {meta.subjects.length} subjects · {concepts?.length ?? 0} published concepts — each explained at four levels, from curious to expert.
            </p>
            <div className="mt-4">
              <GuidePdfDownload url={`${API_BASE}/api/science/pillars/${pillar}/guide.pdf`}
                label="Classroom guide (PDF)" testid="pillar-guide-pdf" color="#1E40AF" />
            </div>

            {/* Published concepts first — the usable content */}
            {concepts && concepts.length > 0 && (
              <div className="mt-8" data-testid="pillar-concepts">
                <h2 className="font-serif-display text-2xl mb-3">Concepts</h2>
                <div className="grid gap-3 md:grid-cols-2">
                  {concepts.map((c) => (
                    <div key={c.concept_id}>
                      <Link to={`/science/concept/${c.concept_id}`} data-testid={`pillar-concept-${c.concept_id}`}
                            className="group block rounded-2xl bg-white border border-black/10 p-5 hover:border-[color:var(--np-brand,#B85F2A)] transition-colors">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-serif-display text-xl leading-tight">{c.title}</div>
                          <ArrowRight size={14} weight="bold" className="text-[color:var(--np-brand,#B85F2A)] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                        </div>
                        <p className="mt-1 text-sm text-[#3A3830] line-clamp-2">{c.short_definition}</p>
                        <div className="mt-2 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[color:var(--np-muted,#3A3830)]">
                          {(c.content_type || "concept").replace("-", " ")} · {String(c.evidence_label || "").replace("-", " ")}
                        </div>
                      </Link>
                      <Link to={`/read/articles/science-concept-${c.concept_id}`} data-testid={`pillar-concept-story-${c.concept_id}`}
                        className="mt-1 ml-1 inline-block font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#B85F2A] hover:text-[#a25526]">
                        AI story →
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subject map */}
            <div className="mt-10 space-y-6" data-testid="pillar-subjects">
              {meta.subjects.map((s) => (
                <section key={s.slug} className="rounded-2xl bg-white border border-black/10 p-5">
                  <h3 className="font-serif-display text-xl">{s.label}</h3>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {s.topics.map((t) => {
                      const live = (concepts || []).some((c) => c.concept_id === t);
                      return live ? (
                        <Link key={t} to={`/science/concept/${t}`}
                              className="rounded-full bg-[color:var(--np-brand,#B85F2A)]/10 text-[color:var(--np-brand,#B85F2A)] px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.14em] uppercase hover:bg-[color:var(--np-brand,#B85F2A)]/20">
                          {t.replace(/-/g, " ")}
                        </Link>
                      ) : (
                        <span key={t} className="rounded-full border border-black/10 border-dashed px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.14em] uppercase text-[#3A3830]/50">
                          {t.replace(/-/g, " ")}
                        </span>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
            <p className="mt-6 text-xs text-[#3A3830]/60">
              Dashed topics are on the editorial map but not yet written — the knowledge graph grows concept by concept.
            </p>
          </>
        )}
      </div>
    </main>
  );
}
